import { useEffect, useState } from "react"
import { Grid, Skeleton } from "@mui/material"
import { useLocation, useNavigate } from 'react-router-dom'
import { ISettingsItem, useSettings } from "../../../Settings"
import { usePlan } from '../../../Plans'
import { useForm } from "../../../Form"
import { useInitReq } from "../../../InitReq"
import { useTranslation } from "../../../i18n"
import { ReactComponent as RightArrow } from '../../../assets/icons/rightArrow.svg'
import SwitchDefault from "../../SwitchDefault"
import NumberCounter from "../../NumberCounter"
import VoucherValidation from "./VoucherValidation"

export default function SelectPlanDetails({ name, variables }: ISettingsItem) {
  const {
    monthlyPrice,
    annualPrice,
    chipOnlyPrice,
    updatePlanState,
    portability,
    setPortability,
    loadingPlan,
    chipOnly
  } = usePlan()
  const [currentPath, setCurrentPath] = useState<string>()
  const { currentFields: { isVoucherValid }, updateFormState } = useForm()
  const { pageTitle, voucherClub, isSurfMVNE } = useSettings()
  const { loadingChipReq, loadingPhysical, loadingVirtual, annualPlanSaleEnabled } = useInitReq()
  const { t } = useTranslation()
  const { text } = variables || {}
  const location = useLocation()
  const visibleVoucherPath = currentPath === '' || currentPath === 'vip'
  const currentChipOnlyPrice = chipOnlyPrice.toFixed(2).replace('.', ',')
  const currentMonthlyPrice = monthlyPrice.toFixed(2).replace('.', ',')
  const currentAnnualPrice = annualPrice.toFixed(2).replace('.', ',')
  const defaultTextArray = [
    t.page1.selectPlanDetails.portabilityOn,
    t.page1.selectPlanDetails.portabilityOff,
    t.page1.selectPlanDetails.gainingInternet,
    t.page1.selectPlanDetails.losingInternet,
    t.page1.selectPlanDetails.losingBadge,
    t.page1.selectPlanDetails.gainingBadge,
    t.page1.selectPlanDetails.mostPopular,
    t.page1.selectPlanDetails.renewMonthly,
    t.page1.selectPlanDetails.mostProfitable,
    t.page1.selectPlanDetails.buyTwelveForEight,
    'flex', // choosePortability
    'flex', // switch
    'flex', // cards
    'none' // detail
  ]
  const mergedArray = text
    ? text.map((value, index) => (value !== undefined && value !== '' ? value : defaultTextArray[index]))
    : defaultTextArray

  const navigate = useNavigate()
  const handleCardClick = (chosen: string) => {
    navigate('/configure-seu-plano')
    updatePlanState({ chosenPlan: chosen, virtualChip: chosen === 'annual' ? true : false })
    updateFormState(prevState => ({ ...prevState, paymentOption: chosen === 'monthly' ? 'oneTime' : prevState.paymentOption })) 
  }

  const handleSwitchChange = () => {
    setPortability(!portability)
  }

  useEffect(() => {
    const param = location.pathname.split('/')[1]
    
    setCurrentPath(param)
  }, [location])

  return (
    <Grid container item className={name ? `${name} select-plan-details` : "select-plan-details"} xs={12}>
      {!chipOnly && (
        <Grid container item className="choosePortability" xs={12} style={{ display: mergedArray[10] }}>
          <Grid item className="choosePortabilitySwitch" style={{ display: mergedArray[11] }}>
            {!loadingPlan ? (
              <SwitchDefault checked={portability} onChange={handleSwitchChange} />
            ) : (
              <Skeleton className="skltn-switch" sx={{ borderRadius: '20px' }} variant="rounded" width="36px" height="21px" />
            )}
          </Grid>
          <Grid item className="choosePortabilityText">
            <h3>{portability ? mergedArray[0] : mergedArray[1]}</h3>
            {/* Para MVNEs Surf não há bônus +5GB: ocultar texto de ganho/perda de internet */}
            {!isSurfMVNE && (
              <p>{portability ? mergedArray[2] : mergedArray[3]}</p>
            )}
          </Grid>
          {/* Para MVNEs Surf não exibir badge de "GANHAR 5GB"/"PERDER 5GB" */}
          {!isSurfMVNE && (
            mergedArray[13] === 'none' ? (
              !portability && (
                <Grid item className="choosePortabilityDetail">
                  <p>{portability ? mergedArray[4] : mergedArray[5]}</p>
                </Grid>
              )
            ) : (
              <Grid item className="choosePortabilityDetail">
                <p>{portability ? mergedArray[4] : mergedArray[5]}</p>
              </Grid>
            )
          )}
        </Grid>
      )}
      {(voucherClub && !isVoucherValid && visibleVoucherPath) && <VoucherValidation />}
      {!loadingPlan && !loadingChipReq && !loadingPhysical && !loadingVirtual ? (
        <Grid container item className="monthlyAnnualCards" sx={{ display: mergedArray[12] }}>
          {chipOnly ? (
            <Grid container item className="monthlyAnnualCard chipOnly monthly" onClick={() => handleCardClick('chipOnly')}>
              <Grid container item className="monthlyAnnualChipCounter" xs={2} onClick={(e) => e.stopPropagation()}>
                <NumberCounter />
              </Grid>
              <Grid container item className="monthlyAnnualTextArea" xs={6}>
                <Grid container item className="monthlyAnnualTitle">
                  <h3>{t.page1.selectPlanDetails.chipPhysical}</h3>
                </Grid>
                <p>{pageTitle ? `${t.page1.selectPlanDetails.becomeClient} ${pageTitle.split(" ")[0]}` : t.page1.selectPlanDetails.becomeClientFallback}</p>
              </Grid>
              <Grid item className="monthlyAnnualPriceArea" xs={4}>
                <Grid item className="monthlyPriceArea">
                  <h3>R$ {currentChipOnlyPrice}</h3>
                </Grid>
                <RightArrow />
              </Grid>
            </Grid>
          ) : (
            <>
              <Grid container item className="monthlyAnnualCard monthly" onClick={() => handleCardClick('monthly')}>
                <Grid container item className="monthlyAnnualTextArea" xs={8}>
                  <Grid container item className="monthlyAnnualTitle">
                    <h3>{t.page1.selectPlanDetails.monthly}</h3>
                    <Grid item className="monthlyAnnualDetails monthlyDetails">{mergedArray[6]}</Grid>
                  </Grid>
                  <p>{mergedArray[7]}</p>
                </Grid>
                <Grid item className="monthlyAnnualPriceArea" xs={4}>
                  <Grid item className="monthlyPriceArea">
                    <h3>R$ {currentMonthlyPrice}</h3>
                  </Grid>
                  <RightArrow />
                </Grid>
              </Grid>
              {annualPlanSaleEnabled && (
                <Grid container item className="monthlyAnnualCard annual" onClick={() => handleCardClick('annual')}>
                  <Grid container item className="monthlyAnnualTextArea" xs={8}>
                    <Grid container item className="monthlyAnnualTitle">
                      <h3>{t.page1.selectPlanDetails.annual}</h3>
                      <Grid item className="monthlyAnnualDetails annualDetails">{mergedArray[8]}</Grid>
                    </Grid>
                    <p>{mergedArray[9]}</p>
                  </Grid>
                  <Grid container item className="monthlyAnnualPriceArea" xs={4}>
                    <Grid item className="annualPriceArea">
                      <h3 className="oldPrice">R$ {currentMonthlyPrice}</h3>
                      <h3>R$ {currentAnnualPrice}</h3>
                    </Grid>
                    <RightArrow />
                  </Grid>
                </Grid>
              )}
            </>
          )}
        </Grid>
      ) : (
        <Grid container item className="monthlyAnnualCards" sx={{ display: mergedArray[12] }}>
          <Skeleton className="skltn-monthlyAnnualCard" variant="rounded" width="100%" />
          {annualPlanSaleEnabled && (
            <Skeleton className="skltn-monthlyAnnualCard" variant="rounded" width="100%" />
          )}
        </Grid>
      )}
    </Grid>
  )
}